// EXPANDABLE_DRAWER_IMPLEMENTED_2026_02_28
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import MarkdownIt from 'markdown-it';
import { opportunityApi, aiApi, watchListApi, pipelineApi, documentApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useChatWebSocket } from '../../hooks/useChatWebSocket';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { ConversationHistory } from '../../components/chat/ConversationHistory';
import { 
  BuildingOfficeIcon, DocumentTextIcon,
  UserIcon, LinkIcon, MapPinIcon,
  StarIcon, ChatBubbleLeftRightIcon,
  PaperAirplaneIcon, SparklesIcon, DocumentIcon, ArrowDownTrayIcon,
  ChartBarIcon, EyeIcon, CheckIcon, BookmarkIcon,
  CloudIcon,
  ClockIcon,
  MicrophoneIcon,
  StopIcon,
  TagIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  XMarkIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface Opportunity {
  id: string;
  title: string;
  agency: string;
  subAgency?: string;
  agencyHierarchy?: {
    department?: string;
    subTier?: string;
    majorCommand?: string;
    subCommand1?: string;
    subCommand2?: string;
    subCommand3?: string;
    office?: string;
  };
  solicitationNumber?: string;
  contractType: string;
  type?: string;
  baseType?: string;
  setAside?: string;
  setAsideCode?: string;
  value: number;
  naicsCode: string;
  naicsCodes?: string[];
  postedDate: string;
  responseDeadline: string;
  archiveDate?: string;
  archiveType?: string;
  status: string;
  active?: string;
  description: string;
  fullDescription?: string;
  classificationCode?: string;
  noticeId?: string;
  fullParentPathName?: string;
  fullParentPathCode?: string;
  placeOfPerformance?: {
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  office?: string;
  uiLink?: string;
  ui_link?: string;  // Database field name fallback
  links?: string[];
  resourceLinks?: Array<{ url?: string; description?: string }>;
  pointOfContact?: Array<{
    title?: string;
    fullName?: string;
    email?: string;
    phone?: string;
    fax?: string;
    type?: string;
  }>;
  attachments?: Array<{ name: string; size?: number; url?: string }>;
  award?: {
    date?: string;
    number?: string;
    amount?: number;
    awardee?: { name?: string; ueiSAM?: string };
  };
  // Additional CSV fields
  subTier?: string;
  fpdsCode?: string;
  organizationType?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
}

// Note: ChatMessage is now imported from useChat hook

const SAMPLE_QUESTIONS = [
  "What are the key requirements for this contract?",
  "Is my company eligible to bid?",
  "What's the evaluation criteria?",
  "What documents do I need to submit?",
];

const TOPICS = [
  { id: 'general', label: 'General Information', icon: DocumentTextIcon },
  { id: 'compliance', label: 'Compliance', icon: ChartBarIcon },
  { id: 'pricing', label: 'Pricing', icon: TagIcon },
  { id: 'subcontracting', label: 'Subcontracting', icon: BuildingOfficeIcon },
  { id: 'submission', label: 'Bid Submission', icon: PaperAirplaneIcon },
  { id: 'proposal', label: 'Proposal Writing', icon: DocumentIcon },
];

// Markdown renderer instance
const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
});

// Helper component to render markdown
const MarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  const html = useMemo(() => md.render(content || ''), [content]);
  return <div className="markdown-content" dangerouslySetInnerHTML={{ __html: html }} />;
};

const OpportunityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiSummary, setAiSummary] = useState<string>('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Chat state with OpenClaw WebSocket streaming
  const {
    messages: chatMessages,
    isLoading: isChatLoading,
    error: chatError,
    sendMessage: sendChatMessage,
    loadConversation,
    creditsUsed,
    creditsTotal,
    isConnected: isChatConnected,
    transport: chatTransport,
    conversationId: chatConversationId,
  } = useChatWebSocket();
  
  // Conversation history sidebar state
  const [showHistory, setShowHistory] = useState(false);
  
  // Chat expanded drawer state
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  
  // Selected topic for agent routing
  const [selectedTopic, setSelectedTopic] = useState<string>('general');
  
  // Voice input
  const {
    isListening,
    transcript: voiceTranscript,
    interimTranscript,
    error: voiceError,
    isSupported: isVoiceSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition();
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  // Watch List & Pipeline state
  const [isInWatchList, setIsInWatchList] = useState(false);
  const [isInPipeline, setIsInPipeline] = useState(false);
  const [watchListLoading, setWatchListLoading] = useState(false);
  
  // Document filenames state
  const [documentFilenames, setDocumentFilenames] = useState<Record<string, string>>({});
  const [savedDocuments, setSavedDocuments] = useState<Set<string>>(new Set());
  const [savingDocument, setSavingDocument] = useState<string | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  useEffect(() => {
    fetchOpportunityDetail();
    checkWatchListStatus();
    checkPipelineStatus();
    checkSavedDocuments();
    loadOpportunityConversation();
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchOpportunityDetail = async () => {
    try {
      setLoading(true);
      const response = await opportunityApi.getById(id!);
      console.log('Opportunity data:', JSON.stringify(response.data.opportunity, null, 2));
      const oppData = response.data.opportunity;
      setOpportunity(oppData);
      
      // Fetch document filenames if there are links
      // Fetch document filenames if there are links
      const parsedLinks = parseLinks(oppData?.links);
      const parsedResourceLinks = parseLinks(oppData?.resourceLinks);
      // Normalize URLs - handle both strings and objects with url property
      const allLinks = [
        ...parsedLinks.map(normalizeUrl).filter(Boolean),
        ...parsedResourceLinks.map(normalizeUrl).filter(Boolean)
      ] as string[];
      
      if (allLinks.length > 0) {
        try {
          const filenamesRes = await documentApi.getFilenames(allLinks);
          const filenameMap: Record<string, string> = {};
          filenamesRes.data.data.forEach((item: { url: string; filename: string }) => {
            filenameMap[item.url] = item.filename;
          });
          setDocumentFilenames(filenameMap);
        } catch (err) {
          console.error('Failed to fetch document filenames:', err);
        }
      }
      
      // Fetch AI summary
      setLoadingSummary(true);
      try {
        console.log('Fetching AI summary for:', id);
        const summaryRes = await opportunityApi.getSummary(id!);
        console.log('AI summary received:', summaryRes.data.summary?.substring(0, 100));
        // Ensure summary is always a string
        const summary = summaryRes.data.summary;
        setAiSummary(typeof summary === 'string' ? summary : String(summary || ''));
      } catch (err: any) {
        console.error('Failed to fetch AI summary:', err.response?.data || err.message);
        // Try to use fallback summary from opportunity data
        if (oppData?.description) {
          setAiSummary(`<h3>Summary of Key Points</h3><ul><li>${oppData.title}</li><li>${oppData.setAside || 'Open to all businesses'}</li><li>NAICS Code: ${oppData.naicsCode || 'N/A'}</li></ul><h3>Suggestions</h3><ul><li>Review the full solicitation document</li><li>Verify eligibility requirements</li><li>Prepare proposal before deadline</li></ul>`);
        }
      } finally {
        setLoadingSummary(false);
      }
    } catch (err: any) {
      setError('Failed to load opportunity details');
      console.error('Error fetching opportunity:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || !opportunity || isChatLoading) return;

    // Build SAM.gov URL
    const samUrl = opportunity.uiLink || `https://sam.gov/opp/${opportunity.noticeId || opportunity.id}/view`;

    // Build context from opportunity with topic for agent routing
    const context = {
      opportunityId: opportunity.id,
      opportunity: {
        title: opportunity.title,
        description: opportunity.description,
        agency: opportunity.agency,
        setAside: opportunity.setAside,
        naicsCode: opportunity.naicsCode,
        deadline: opportunity.responseDeadline,
        solicitationNumber: opportunity.solicitationNumber,
        noticeId: opportunity.noticeId || opportunity.id,
        ulink: samUrl,
        sourceUrl: samUrl,
        contractType: opportunity.contractType,
        placeOfPerformance: opportunity.placeOfPerformance ? 
          `${opportunity.placeOfPerformance.city || ''}, ${opportunity.placeOfPerformance.state || ''} ${opportunity.placeOfPerformance.zip || ''}, ${opportunity.placeOfPerformance.country || 'USA'}` : 
          'N/A',
        estimatedValue: opportunity.value ? `$${opportunity.value.toLocaleString()}` : 'N/A',
      },
      topic: selectedTopic !== 'general' ? selectedTopic : undefined,
    };

    const message = chatInput;
    setChatInput('');
    await sendChatMessage(message, context);
  };

  const handleSampleQuestion = (question: string) => {
    setChatInput(question);
  };

  // Sync voice transcript with chat input
  useEffect(() => {
    if (voiceTranscript) {
      setChatInput(voiceTranscript);
    }
  }, [voiceTranscript]);

  // Handle voice send - stop listening and send message
  const handleVoiceSend = () => {
    stopListening();
    if (voiceTranscript.trim()) {
      setChatInput(voiceTranscript);
      // Small delay to ensure state is updated
      setTimeout(() => {
        handleSendMessage();
        resetTranscript();
      }, 100);
    }
  };

  // Check if opportunity is in watch list
  const checkWatchListStatus = async () => {
    try {
      const response = await watchListApi.checkStatus(id!);
      setIsInWatchList(response.data.data.isInWatchList);
    } catch (err) {
      // Silently fail - user might not be logged in
    }
  };

  // Check if opportunity is in pipeline
  const checkPipelineStatus = async () => {
    try {
      const response = await pipelineApi.getByOpportunityId(id!);
      setIsInPipeline(!!response.data.data);
    } catch (err: any) {
      // 404 means not in pipeline, which is fine
      if (err.response?.status === 404) {
        setIsInPipeline(false);
      }
      // Otherwise silently fail
    }
  };

  // Check which documents are already saved to AI Document Hub
  const checkSavedDocuments = async () => {
    if (!id) return;
    try {
      const response = await aiApi.checkOpportunityDocuments(id);
      const savedUrls = response.data.data.savedUrls || [];
      setSavedDocuments(new Set(savedUrls));
    } catch (err) {
      // Silently fail - user might not be logged in or on free plan
    }
  };

  // Load existing conversation for this opportunity
  const loadOpportunityConversation = async () => {
    if (!id) {
      console.log('[ChatHistory] No opportunity ID, skipping');
      return;
    }
    try {
      console.log('[ChatHistory] Fetching conversations for opportunity:', id);
      // Fetch all user conversations
      const response = await aiApi.getConversations();
      const conversations = response.data.data || [];
      console.log('[ChatHistory] Total conversations:', conversations.length);
      
      // Find conversations for this opportunity
      const oppConversations = conversations.filter(
        (c: any) => {
          console.log('[ChatHistory] Checking conversation:', c.id, 'oppId:', c.opportunityId, 'match:', c.opportunityId === id);
          return c.opportunityId === id;
        }
      );
      
      console.log('[ChatHistory] Found conversations for this opportunity:', oppConversations.length);
      
      if (oppConversations.length > 0) {
        // Sort by updatedAt (most recent first)
        oppConversations.sort((a: any, b: any) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        
        // Load the most recent conversation
        const mostRecent = oppConversations[0];
        console.log('[ChatHistory] Loading most recent conversation:', mostRecent.id);
        await loadConversation(mostRecent.id);
        console.log('[ChatHistory] Conversation loaded successfully');
      } else {
        console.log('[ChatHistory] No conversations found for this opportunity');
      }
    } catch (err) {
      // Silently fail - user might not be logged in or on free plan
      console.error('[ChatHistory] Error loading conversation:', err);
    }
  };

  // Add to Watch List
  const handleAddToWatchList = async () => {
    setWatchListLoading(true);
    try {
      await watchListApi.add(id!);
      setIsInWatchList(true);
      showActionMessage('success', 'Added to Watch List');
    } catch (err: any) {
      if (err.response?.status === 409) {
        setIsInWatchList(true);
        showActionMessage('success', 'Already in Watch List');
      } else if (err.response?.data?.error?.message) {
        showActionMessage('error', err.response.data.error.message);
      } else {
        showActionMessage('error', 'Failed to add to Watch List');
      }
    } finally {
      setWatchListLoading(false);
    }
  };

  // Remove from Watch List
  const handleRemoveFromWatchList = async () => {
    setWatchListLoading(true);
    try {
      await watchListApi.remove(id!);
      setIsInWatchList(false);
      showActionMessage('success', 'Removed from Watch List');
    } catch (err) {
      showActionMessage('error', 'Failed to remove from Watch List');
    } finally {
      setWatchListLoading(false);
    }
  };

  // Add to Pipeline
  const handleAddToPipeline = async () => {
    setPipelineLoading(true);
    try {
      await pipelineApi.addOpportunity(id!);
      setIsInPipeline(true);
      showActionMessage('success', 'Added to Pipeline');
    } catch (err: any) {
      if (err.response?.status === 409) {
        setIsInPipeline(true);
        showActionMessage('success', 'Already in Pipeline');
      } else if (err.response?.data?.error?.message) {
        showActionMessage('error', err.response.data.error.message);
      } else {
        showActionMessage('error', 'Failed to add to Pipeline');
      }
    } finally {
      setPipelineLoading(false);
    }
  };

  // Show action message
  const showActionMessage = (type: 'success' | 'error', text: string) => {
    setActionMessage({ type, text });
    setTimeout(() => setActionMessage(null), 3000);
  };

  // Save document to AI Document Hub
  const handleSaveDocument = async (url: string, filename: string) => {
    if (!opportunity) return;
    
    setSavingDocument(url);
    try {
      await aiApi.saveDocumentFromOpportunity({
        documentUrl: url,
        filename: filename,
        noticeId: opportunity.noticeId || opportunity.id,
        solicitationNumber: opportunity.solicitationNumber,
        title: opportunity.title,
        agency: opportunity.agency,
        source: 'opportunity',
      });
      
      setSavedDocuments(prev => new Set(prev).add(url));
      showActionMessage('success', `Saved "${filename}" to AI Document Hub`);
    } catch (err: any) {
      if (err.response?.status === 403) {
        showActionMessage('error', 'AI Document Hub requires PRO or ENTERPRISE plan');
      } else {
        showActionMessage('error', 'Failed to save document');
      }
    } finally {
      setSavingDocument(null);
    }
  };

  // Check if description is meaningful (not a URL or empty)
  const hasMeaningfulDescription = (desc?: string): boolean => {
    if (!desc) return false;
    if (desc.trim().length < 20) return false;
    if (desc.startsWith('http://') || desc.startsWith('https://')) return false;
    return true;
  };

  // Parse links from opportunity (handles JSON string, array of strings, or array of objects)
  const parseLinks = (linksData: any): (string | { url?: string; description?: string })[] => {
    if (!linksData) return [];
    if (Array.isArray(linksData)) return linksData;
    if (typeof linksData === 'string') {
      try {
        const parsed = JSON.parse(linksData);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  
  // Normalize URL - handles both string URLs and objects with url property
  const normalizeUrl = (link: any): string | null => {
    if (!link) return null;
    if (typeof link === 'string') return link;
    if (typeof link === 'object' && link.url) return link.url;
    return null;
  };

  // Get document name - uses fetched filename from API or extracts from URL
  const getDocumentNameFromUrl = (url: any): string => {
    // Handle non-string inputs
    if (!url || typeof url !== 'string') return 'Document';
    
    // First check if we have the actual filename from the API
    if (documentFilenames[url]) {
      return documentFilenames[url];
    }
    
    // Fallback: Try to extract file ID from SAM.gov API URLs
    // Pattern: https://sam.gov/api/prod/opps/v3/opportunities/resources/files/{fileId}/download
    const samGovMatch = url.match(/\/files\/([a-f0-9-]+)\/download$/i);
    if (samGovMatch) {
      const fileId = samGovMatch[1];
      // Return shortened file ID (first 8 chars)
      return `File ${fileId.substring(0, 8)}...`;
    }
    
    // Try to extract filename from URL
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop();
      if (filename && filename.length > 0) {
        // Remove query params if any
        return filename.split('?')[0];
      }
    } catch {
      // Invalid URL, fall through
    }
    
    // Fallback: return shortened URL
    return url.length > 40 ? url.substring(0, 40) + '...' : url;
  };

  // Get SAM.gov URL from opportunity
  const getSamGovUrl = (): string => {
    // Use uiLink or ui_link from database
    if (opportunity?.uiLink) return opportunity.uiLink;
    if (opportunity?.ui_link) return opportunity.ui_link;
    // Fallback to constructed URL
    return `https://sam.gov/opp/${opportunity?.noticeId || opportunity?.id || ''}/view`;
  };

  const documentLinks = parseLinks(opportunity?.links);
  const resourceLinks = parseLinks(opportunity?.resourceLinks);

  const formatCurrency = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getDaysRemaining = (deadline?: string) => {
    if (!deadline) return null;
    const end = new Date(deadline);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
    </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Opportunity not found'}</p>
        <Link to="/opportunities" className="mt-4 text-brand-600 hover:text-brand-700">← Back to Opportunities</Link>
    </div>
    );
  }

  const daysRemaining = getDaysRemaining(opportunity.responseDeadline);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb -->
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/opportunities" className="hover:text-brand-600 flex items-center gap-1">
              <ArrowLeftIcon className="h-4 w-4" /> Opportunities
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium truncate">{typeof opportunity.solicitationNumber === 'string' ? opportunity.solicitationNumber : (typeof opportunity.id === 'string' ? opportunity.id : 'Detail')}</span>
          </div>
        </div>
    </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Top Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-6">
              <button onClick={() => setActiveTab('overview')} className={`flex items-center gap-2 text-sm font-medium ${activeTab === 'overview' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-gray-600 hover:text-gray-900'}`}>
                <DocumentTextIcon className="h-4 w-4" /> Contract Overview
              </button>
              <button onClick={() => setActiveTab('general')} className={`flex items-center gap-2 text-sm font-medium ${activeTab === 'general' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-gray-600 hover:text-gray-900'}`}>
                <BuildingOfficeIcon className="h-4 w-4" /> General Information
              </button>
              <button onClick={() => setActiveTab('compliance')} className={`flex items-center gap-2 text-sm font-medium ${activeTab === 'compliance' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-gray-600 hover:text-gray-900'}`}>
                <ChartBarIcon className="h-4 w-4" /> Compliance Score
              </button>
            </div>
            <div className="flex items-center gap-2">
              {/* Action Message Toast */}
              {actionMessage && (
                <div className={`px-3 py-1.5 rounded-lg text-sm ${actionMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {actionMessage.text}
                </div>
              )}
              
              {/* Watch List Button */}
              <button 
                onClick={isInWatchList ? handleRemoveFromWatchList : handleAddToWatchList}
                disabled={watchListLoading}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                  isInWatchList 
                    ? 'text-amber-600 border-amber-300 bg-amber-50 hover:bg-amber-100' 
                    : 'text-gray-600 hover:text-gray-900 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {watchListLoading ? (
                  <div className="animate-spin h-4 w-4 border border-gray-300 border-t-amber-500 rounded-full" />
                ) : isInWatchList ? (
                  <>
                    <CheckIcon className="h-4 w-4" /> In Watch List
                  </>
                ) : (
                  <>
                    <EyeIcon className="h-4 w-4" /> Add to Watch List
                  </>
                )}
              </button>
              
              {/* Pipeline Button */}
              <button 
                onClick={handleAddToPipeline}
                disabled={pipelineLoading || isInPipeline}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                  isInPipeline 
                    ? 'text-brand-600 border-brand-300 bg-brand-50 cursor-default' 
                    : 'text-gray-600 hover:text-gray-900 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {pipelineLoading ? (
                  <div className="animate-spin h-4 w-4 border border-gray-300 border-t-brand-500 rounded-full" />
                ) : isInPipeline ? (
                  <>
                    <CheckIcon className="h-4 w-4" /> In Pipeline
                  </>
                ) : (
                  <>
                    <StarIcon className="h-4 w-4" /> Add to Pipeline
                  </>
                )}
              </button>
              
              <a 
                href={getSamGovUrl()} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <LinkIcon className="h-4 w-4" /> View on SAM.gov
              </a>
            </div>
          </div>
        </div>

        {/* Opportunity Title and Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900 leading-tight">
                {opportunity.title || 'Untitled Opportunity'}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  opportunity.active === 'Yes' || 
                  opportunity.status?.toLowerCase() === 'active' || 
                  opportunity.status?.toLowerCase() === 'open'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {opportunity.active === 'Yes' || 
                   opportunity.status?.toLowerCase() === 'active' || 
                   opportunity.status?.toLowerCase() === 'open' ? 'Open' : (opportunity.status || 'Closed')}
                </span>
                {opportunity.solicitationNumber && (
                  <span className="text-sm text-gray-500">
                    Solicitation: <span className="font-mono">{opportunity.solicitationNumber}</span>
                  </span>
                )}
                {opportunity.naicsCode && (
                  <span className="text-sm text-gray-500">
                    NAICS: <span className="font-mono">{opportunity.naicsCode}</span>
                  </span>
                )}
              </div>
            </div>
            {daysRemaining !== null && (
              <div className={`text-right ${daysRemaining < 7 ? 'text-red-600' : 'text-gray-600'}`}>
                <p className="text-2xl font-bold">{daysRemaining}</p>
                <p className="text-xs">days left</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Summary Card - Always visible */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-4 py-3 border-b border-purple-100">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">AI Contract Analysis</h3>
                </div>
              </div>
              <div className="p-4">
                {loadingSummary ? (
                  <div className="flex items-center gap-3 text-gray-500">
                    <div className="animate-spin h-5 w-5 border border-gray-300 border-t-purple-500 rounded-full"></div>
                    <span>Analyzing contract with AI...</span>
                  </div>
                ) : aiSummary ? (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: typeof aiSummary === 'string' ? aiSummary : String(aiSummary || '') }} />
                ) : (
                  <p className="text-gray-500 text-sm">AI analysis not available for this opportunity.</p>
                )}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <>
                {/* Overview Tab: Quick Info Cards */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="relative w-24 h-24">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                          <path className="text-gray-200" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                          <path className="text-green-500" strokeDasharray="75, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-900">75</span>
                        </div>
                      </div>
                      <p className="text-xs text-center text-gray-500 mt-1">Match Score</p>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-2">Opportunity Overview</h4>
                      <p className="text-sm text-gray-600">
                        {typeof opportunity.title === 'string' ? opportunity.title : 'Untitled Opportunity'} from {opportunity.agency || 'Unknown Agency'}.
                        {opportunity.setAside && opportunity.setAside !== 'None' && ` ${opportunity.setAside} set-aside.`}
                        {opportunity.naicsCode && ` NAICS Code: ${opportunity.naicsCode}.`}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <button className="btn-primary text-sm">Build Proposal</button>
                    </div>
                  </div>
                </div>

                {/* Key Details Grid */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Key Details</h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Solicitation Number</p>
                        <p className="text-sm font-medium">{opportunity.solicitationNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Type</p>
                        <p className="text-sm font-medium">{opportunity.type || 'Solicitation'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">NAICS</p>
                        <p className="text-sm font-medium">{opportunity.naicsCode || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Place of Performance</p>
                        <p className="text-sm font-medium">
                          {typeof opportunity.placeOfPerformance?.state === 'string' ? opportunity.placeOfPerformance.state : 'N/A'}, {typeof opportunity.placeOfPerformance?.country === 'string' ? opportunity.placeOfPerformance.country : 'USA'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Set-Aside</p>
                        <p className="text-sm font-medium">{opportunity.setAside || 'None'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Response Deadline</p>
                        <p className={`text-sm font-medium ${daysRemaining !== null && daysRemaining < 7 ? 'text-red-600' : ''}`}>
                          {formatDate(opportunity.responseDeadline)}
                          {daysRemaining !== null && <span className="ml-2 text-xs">({daysRemaining} days)</span>}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Description</h2>
                  </div>
                  <div className="p-6">
                    {hasMeaningfulDescription(opportunity.description) ? (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                        <p className="text-slate-700 leading-7 text-base whitespace-pre-wrap">
                          {typeof opportunity.description === 'string' ? opportunity.description : String(opportunity.description)}
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
                          <DocumentTextIcon className="h-8 w-8 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Description Data Coming Soon</h3>
                        <p className="text-gray-500 mb-4 max-w-md mx-auto">
                          We're still processing the full description for this opportunity. 
                          Please check back later or view the details on SAM.gov.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={isInWatchList ? handleRemoveFromWatchList : handleAddToWatchList}
                            disabled={watchListLoading}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                              isInWatchList
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                : 'bg-amber-600 text-white hover:bg-amber-700'
                            }`}
                          >
                            {watchListLoading ? (
                              <div className="animate-spin h-4 w-4 border border-white border-t-transparent rounded-full" />
                            ) : isInWatchList ? (
                              <><CheckIcon className="h-4 w-4" /> In Watch List</>
                            ) : (
                              <><EyeIcon className="h-4 w-4" /> Add to Watch List</>
                            )}
                          </button>
                          <a
                            href={getSamGovUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                          >
                            <LinkIcon className="h-4 w-4" /> View on SAM.gov
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Documents */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Contract Documents & Links</h2>
                  </div>
                  <div className="divide-y divide-gray-200">
                    <a 
                      href={getSamGovUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <LinkIcon className="h-5 w-5 text-blue-500" />
                        <span className="text-sm text-brand-600 hover:underline">View Opportunity on SAM.gov</span>
                      </div>
                      <ArrowDownTrayIcon className="h-4 w-4 text-gray-400" />
                    </a>
                    
                    {/* Resource Links from API */}
                    {resourceLinks.length > 0 ? (
                      resourceLinks.map((link: any, index: number) => {
                        const url = normalizeUrl(link);
                        if (!url) return null;
                        const filename = getDocumentNameFromUrl(url);
                        const isSaved = savedDocuments.has(url);
                        return (
                          <div 
                            key={`res-${index}`}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                          >
                            <a 
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 flex-1"
                              title={url}
                            >
                              <DocumentIcon className="h-5 w-5 text-red-500" />
                              <span className="text-sm text-brand-600 hover:underline">{filename}</span>
                            </a>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSaveDocument(url, filename)}
                                disabled={savingDocument === url || isSaved || !isInPipeline}
                                className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                                  isSaved ? 'text-green-600' : isInPipeline ? 'text-gray-400 hover:text-brand-600' : 'text-gray-200 cursor-not-allowed'
                                }`}
                                title={
                                  isSaved 
                                    ? 'Saved to AI Document Hub' 
                                    : !isInPipeline 
                                      ? 'Add to Pipeline to save documents' 
                                      : 'Save to AI Document Hub'
                                }
                              >
                                <BookmarkIcon className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                                title="Download"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        );
                      })
                    ) : null}
                    
                    {/* Links from database */}
                    {documentLinks.length > 0 ? (
                      documentLinks.map((link: any, index: number) => {
                        const url = normalizeUrl(link);
                        if (!url) return null;
                        const filename = getDocumentNameFromUrl(url);
                        const isSaved = savedDocuments.has(url);
                        return (
                          <div 
                            key={`link-${index}`}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                          >
                            <a 
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 flex-1"
                              title={url}
                            >
                              <DocumentIcon className="h-5 w-5 text-blue-500" />
                              <span className="text-sm text-brand-600 hover:underline">{filename}</span>
                            </a>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSaveDocument(url, filename)}
                                disabled={savingDocument === url || isSaved || !isInPipeline}
                                className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                                  isSaved ? 'text-green-600' : isInPipeline ? 'text-gray-400 hover:text-brand-600' : 'text-gray-200 cursor-not-allowed'
                                }`}
                                title={
                                  isSaved 
                                    ? 'Saved to AI Document Hub' 
                                    : !isInPipeline 
                                      ? 'Add to Pipeline to save documents' 
                                      : 'Save to AI Document Hub'
                                }
                              >
                                <BookmarkIcon className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                                title="Download"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        );
                      })
                    ) : null}
                    
                    {/* Attachments */}
                    {opportunity.attachments && opportunity.attachments.length > 0 ? (
                      opportunity.attachments.map((doc: any, index: number) => {
                        const url = doc.url || '#';
                        const filename = typeof doc?.name === 'string' ? doc.name : `Attachment ${index + 1}`;
                        const isSaved = savedDocuments.has(url);
                        return (
                          <div 
                            key={`att-${index}`}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                          >
                            <a 
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 flex-1"
                            >
                              <DocumentIcon className="h-5 w-5 text-orange-500" />
                              <span className="text-sm text-brand-600 hover:underline">{filename}</span>
                            </a>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSaveDocument(url, filename)}
                                disabled={savingDocument === url || isSaved || url === '#' || !isInPipeline}
                                className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                                  isSaved ? 'text-green-600' : isInPipeline ? 'text-gray-400 hover:text-brand-600' : 'text-gray-200 cursor-not-allowed'
                                } ${url === '#' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={
                                  isSaved 
                                    ? 'Saved to AI Document Hub' 
                                    : !isInPipeline 
                                      ? 'Add to Pipeline to save documents' 
                                      : 'Save to AI Document Hub'
                                }
                              >
                                <BookmarkIcon className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                                title="Download"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        );
                      })
                    ) : null}
                    
                    {/* No documents message */}
                    {resourceLinks.length === 0 && documentLinks.length === 0 && (!opportunity.attachments || opportunity.attachments.length === 0) && (
                      <div className="px-4 py-3 text-sm text-gray-500">No additional documents available. Visit SAM.gov for full documentation.</div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'general' && (
              <>
                {/* Agency Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <BuildingOfficeIcon className="h-5 w-5 text-brand-600" />
                      Agency Information
                    </h2>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Department</p>
                      <p className="text-sm font-medium">{opportunity.agency || 'N/A'}</p>
                    </div>
                    {opportunity.subTier && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Sub-Tier</p>
                        <p className="text-sm font-medium">{opportunity.subTier}</p>
                      </div>
                    )}
                    {opportunity.subAgency && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Sub-Agency</p>
                        <p className="text-sm font-medium">{opportunity.subAgency}</p>
                      </div>
                    )}
                    {opportunity.office && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Office</p>
                        <p className="text-sm font-medium">{opportunity.office}</p>
                      </div>
                    )}
                    {opportunity.fpdsCode && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase">FPDS Code</p>
                        <p className="text-sm font-medium">{opportunity.fpdsCode}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Place of Performance */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <MapPinIcon className="h-5 w-5 text-brand-600" />
                      Place of Performance
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">City</p>
                        <p className="text-sm font-medium">{opportunity.placeOfPerformance?.city || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">State</p>
                        <p className="text-sm font-medium">{opportunity.placeOfPerformance?.state || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">ZIP</p>
                        <p className="text-sm font-medium">{opportunity.placeOfPerformance?.zip || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Country</p>
                        <p className="text-sm font-medium">{opportunity.placeOfPerformance?.country || 'USA'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <UserIcon className="h-5 w-5 text-brand-600" />
                      Point of Contact
                    </h2>
                  </div>
                  <div className="p-4">
                    {opportunity.primaryContactName || opportunity.primaryContactEmail ? (
                      <div className="space-y-3">
                        {opportunity.primaryContactName && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Name</p>
                            <p className="text-sm font-medium">{opportunity.primaryContactName}</p>
                          </div>
                        )}
                        {opportunity.primaryContactEmail && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Email</p>
                            <a href={`mailto:${opportunity.primaryContactEmail}`} className="text-sm font-medium text-brand-600 hover:underline">
                              {opportunity.primaryContactEmail}
                            </a>
                          </div>
                        )}
                        {opportunity.primaryContactPhone && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Phone</p>
                            <p className="text-sm font-medium">{opportunity.primaryContactPhone}</p>
                          </div>
                        )}
                      </div>
                    ) : opportunity.pointOfContact && opportunity.pointOfContact.length > 0 ? (
                      <div className="space-y-3">
                        {opportunity.pointOfContact.map((contact: any, idx: number) => (
                          <div key={idx} className="border-b border-gray-100 pb-3 last:border-0">
                            {contact.fullName && <p className="font-medium">{contact.fullName}</p>}
                            {contact.title && <p className="text-sm text-gray-500">{contact.title}</p>}
                            {contact.email && <a href={`mailto:${contact.email}`} className="text-sm text-brand-600 hover:underline">{contact.email}</a>}
                            {contact.phone && <p className="text-sm text-gray-500">{contact.phone}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">Contact information not available. Visit SAM.gov for details.</p>
                    )}
                  </div>
                </div>

                {/* Contract Details */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <DocumentTextIcon className="h-5 w-5 text-brand-600" />
                      Contract Details
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Posted Date</p>
                        <p className="text-sm font-medium">{formatDate(opportunity.postedDate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Response Deadline</p>
                        <p className="text-sm font-medium">{formatDate(opportunity.responseDeadline)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Archive Date</p>
                        <p className="text-sm font-medium">{formatDate(opportunity.archiveDate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Classification Code</p>
                        <p className="text-sm font-medium">{opportunity.classificationCode || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Organization Type</p>
                        <p className="text-sm font-medium">{opportunity.organizationType || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Status</p>
                        <p className="text-sm font-medium">{opportunity.status || 'Open'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'compliance' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Compliance Analysis</h2>
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                      <path className="text-gray-200" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                      <path className="text-green-500" strokeDasharray="75, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold text-gray-900">75</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Your Match Score</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Based on your NAICS codes and past performance
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                    <div>
                      <p className="font-medium">NAICS Code Match</p>
                      <p className="text-sm text-gray-600">Your company NAICS codes align with this opportunity</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                    <div>
                      <p className="font-medium">Set-Aside Eligibility</p>
                      <p className="text-sm text-gray-600">You meet the set-aside requirements</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-yellow-600 text-xs">!</span>
                    </div>
                    <div>
                      <p className="font-medium">Past Performance</p>
                      <p className="text-sm text-gray-600">Consider strengthening your past performance documentation</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - AI Chat */}
          <div className={`transition-all duration-300 ${isChatExpanded ? 'hidden' : ''}`}>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-200px)] grid grid-rows-[auto_auto_auto_1fr_auto] overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600" />
                    <div>
                      <h3 className="font-semibold text-purple-900">Your AI Contracting Consultant</h3>
                      <p className="text-xs text-purple-700 flex items-center gap-1">
                        Ask me anything about this contract
                        {isChatConnected && (
                          <span className="inline-flex items-center gap-1 text-green-600" title="WebSocket connected - Real-time streaming">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-[10px]">Live</span>
                          </span>
                        )}
                        {!isChatConnected && chatTransport === 'sse' && (
                          <span className="inline-flex items-center gap-1 text-blue-600" title="Using SSE streaming">
                            <CloudIcon className="h-3 w-3" />
                            <span className="text-[10px]">SSE</span>
                          </span>
                        )}
                        {chatMessages.length > 0 && chatMessages[chatMessages.length - 1]?.metadata?.usedFallback && (
                          <span className="inline-flex items-center gap-1 text-amber-600" title="Using backup AI (OpenRouter)">
                            <span className="text-[10px]">Fallback AI</span>
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {creditsTotal > 0 && (
                      <div className="text-xs text-purple-700 mr-2">
                        {creditsUsed}/{creditsTotal} credits
                      </div>
                    )}
                    {/* Expand Button */}
                    <button
                      onClick={() => setIsChatExpanded(true)}
                      className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-100 transition-colors"
                      title="Expand chat for full view"
                    >
                      <ArrowsPointingOutIcon className="h-4 w-4" />
                    </button>
                    {/* History Toggle Button */}
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        showHistory 
                          ? 'bg-purple-200 text-purple-800' 
                          : 'text-purple-600 hover:bg-purple-100'
                      }`}
                      title="Conversation history"
                    >
                      <ClockIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Sample Questions */}
              {chatMessages.length === 0 && isInPipeline && (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-2">Sample questions:</p>
                  <div className="space-y-1">
                    {SAMPLE_QUESTIONS.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => handleSampleQuestion(q)}
                        className="block w-full text-left text-xs text-gray-600 hover:text-brand-600 hover:bg-white px-2 py-1 rounded"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Pipeline Required Notice (when no messages) */}
              {chatMessages.length === 0 && !isInPipeline && (
                <div className="p-6 border-b border-gray-200 bg-gray-50 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 mb-3">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-brand-600" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">AI Consultant</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Ask questions about requirements, compliance, pricing, and more.
                  </p>
                  <button
                    onClick={handleAddToPipeline}
                    disabled={pipelineLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white text-xs font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
                  >
                    {pipelineLoading ? (
                      <>
                        <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <StarIcon className="h-3 w-3" />
                        Add to Pipeline to Start
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Topics - Agent Routing */}
              <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-500 mb-2">Select topic for specialized agent:</p>
                <div className="flex flex-wrap gap-1">
                  {TOPICS.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => setSelectedTopic(topic.id)}
                      className={`
                        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors
                        ${selectedTopic === topic.id
                          ? 'bg-brand-100 text-brand-700 border border-brand-300'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                        }
                      `}
                      title={`Route to ${topic.label} specialist`}
                    >
                      <topic.icon className="h-3 w-3" />
                      {topic.label}
                    </button>
                  ))}
                </div>
                {selectedTopic !== 'general' && (
                  <p className="mt-2 text-xs text-brand-600">
                    Using {TOPICS.find(t => t.id === selectedTopic)?.label} specialist
                  </p>
                )}
              </div>

              {/* Chat Content - Messages + Input Wrapper */}
              <div className="h-full min-h-0 flex flex-col overflow-hidden">
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.map((msg, index) => (
                  <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-lg px-4 py-3 text-sm ${
                      msg.role === 'user' 
                        ? 'bg-brand-600 text-white' 
                        : 'bg-gray-100 text-gray-800 border border-gray-200'
                    }`}>
                      {msg.role === 'user' ? (
                        msg.content
                      ) : (
                        <div className="markdown-body prose prose-sm max-w-none">
                          <MarkdownContent content={typeof msg.content === 'string' ? msg.content : String(msg.content || '')} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isChatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-2">
                      <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full"></div>
                      <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full delay-100"></div>
                      <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full delay-200"></div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div className="p-4 border-t border-gray-200">
                {chatError && (
                  <div className="mb-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                    {chatError}
                  </div>
                )}
                
                {/* Pipeline Requirement Notice */}
                {!isInPipeline && (
                  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 text-center">
                      <span className="font-medium">AI Consultant requires Pipeline access.</span>
                      <button
                        onClick={handleAddToPipeline}
                        disabled={pipelineLoading}
                        className="ml-1 underline hover:text-amber-900"
                      >
                        {pipelineLoading ? 'Adding...' : 'Add to Pipeline'}
                      </button>
                    </p>
                  </div>
                )}
                
                {/* Voice Error */}
                {voiceError && isVoiceSupported && (
                  <div className="mb-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    {voiceError}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput + (isListening && interimTranscript ? ' ' + interimTranscript : '')}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder={!isInPipeline 
                      ? "Add to Pipeline to use AI Consultant..." 
                      : isListening
                        ? "Listening..."
                        : isChatLoading 
                          ? "Thinking..." 
                          : "Type your question or use voice..."
                    }
                    disabled={isChatLoading || !isInPipeline}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 disabled:bg-gray-100"
                  />
                  
                  {/* Voice Input Button */}
                  {isVoiceSupported && isInPipeline && (
                    <button
                      onClick={isListening ? handleVoiceSend : startListening}
                      disabled={isChatLoading}
                      className={`px-3 py-2 rounded-lg transition-colors ${
                        isListening 
                          ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } disabled:opacity-50`}
                      title={isListening ? 'Click to send voice message' : 'Use voice input'}
                    >
                      {isListening ? (
                        <StopIcon className="h-4 w-4" />
                      ) : (
                        <MicrophoneIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  
                  <button
                    onClick={handleSendMessage}
                    disabled={!chatInput.trim() || isChatLoading || !isInPipeline}
                    className="px-3 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                    title={!isInPipeline ? "Add to Pipeline to use AI Consultant" : "Send message"}
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <span>
                    {isChatConnected ? '● WebSocket' : chatTransport === 'sse' ? '○ SSE' : '○ Connecting...'} 
                    {' • '}
                    {isChatLoading ? 'Thinking...' : 'Ready'}
                  </span>
                  <span>Uses {creditsUsed}/{creditsTotal} credits</span>
                </div>
              </div>
            </div>
            
            {/* Conversation History Sidebar */}
            <ConversationHistory
              isOpen={showHistory}
              onClose={() => setShowHistory(false)}
              onSelect={(conversation) => {
                loadConversation(conversation.id);
                setShowHistory(false);
              }}
              currentConversationId={chatConversationId}
              opportunityId={opportunity?.id}
            />
          </div>
        </div>
      </div>

      {/* Expanded Chat Drawer Overlay */}
      {isChatExpanded && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={() => setIsChatExpanded(false)}
          />
          
          {/* Drawer Panel */}
          <div className="relative ml-auto w-full max-w-4xl h-full bg-white shadow-2xl grid grid-rows-[auto_auto_auto_1fr_auto] animate-in slide-in-from-right duration-300 overflow-hidden">
            {/* Drawer Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-purple-900">Your Digital Contracting Consultant</h2>
                  <p className="text-sm text-purple-700 flex items-center gap-2">
                    <span>{opportunity?.title || 'Opportunity'}</span>
                    {isChatConnected && (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs" title="WebSocket connected">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        Live
                      </span>
                    )}
                    {!isChatConnected && chatTransport === 'sse' && (
                      <span className="inline-flex items-center gap-1 text-blue-600 text-xs">
                        <CloudIcon className="h-3 w-3" />
                        SSE
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {creditsTotal > 0 && (
                  <div className="px-3 py-1 bg-purple-100 rounded-full text-sm text-purple-700">
                    {creditsUsed}/{creditsTotal} credits
                  </div>
                )}
                {/* History Button */}
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className={`p-2 rounded-lg transition-colors ${
                    showHistory 
                      ? 'bg-purple-200 text-purple-800' 
                      : 'text-purple-600 hover:bg-purple-100'
                  }`}
                  title="Conversation history"
                >
                  <ClockIcon className="h-5 w-5" />
                </button>
                {/* Collapse Button */}
                <button
                  onClick={() => setIsChatExpanded(false)}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                  title="Collapse to sidebar"
                >
                  <ArrowsPointingInIcon className="h-5 w-5" />
                </button>
                {/* Close Button */}
                <button
                  onClick={() => setIsChatExpanded(false)}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-red-600 transition-colors"
                  title="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Quick Opportunity Summary Bar */}
            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Agency:</span>
                <span className="font-medium text-gray-900">{opportunity?.agency || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">NAICS:</span>
                <span className="font-medium text-gray-900">{opportunity?.naicsCode || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Deadline:</span>
                <span className={`font-medium ${daysRemaining !== null && daysRemaining < 7 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatDate(opportunity?.responseDeadline)}
                </span>
              </div>
              <Link 
                to={`/opportunities/${id}`}
                onClick={() => setIsChatExpanded(false)}
                className="ml-auto text-brand-600 hover:text-brand-700 flex items-center gap-1"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Details
              </Link>
            </div>

            {/* Topics - Agent Routing */}
            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500 mb-2">Select topic for specialized agent:</p>
              <div className="flex flex-wrap gap-2">
                {TOPICS.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic.id)}
                    className={`
                      inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                      ${selectedTopic === topic.id
                        ? 'bg-brand-100 text-brand-700 border border-brand-300 shadow-sm'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                      }
                    `}
                    title={`Route to ${topic.label} specialist`}
                  >
                    <topic.icon className="h-4 w-4" />
                    {topic.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Chat Content Area - Messages + Input */}
            <div className="h-full min-h-0 flex flex-col overflow-hidden">
              {/* Chat Messages - Expanded */}
              <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6 bg-gray-50">
                {chatMessages.length === 0 && isInPipeline && (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-purple-100 mb-4">
                      <ChatBubbleLeftRightIcon className="h-10 w-10 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">How can I help you today?</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                      Ask me anything about this opportunity - requirements, compliance, pricing, 
                      subcontracting, or proposal writing.
                    </p>
                  </div>
                )}
                {chatMessages.map((msg, index) => (
                  <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-xl px-6 py-4 shadow-sm ${
                      msg.role === 'user' 
                        ? 'bg-brand-600 text-white' 
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}>
                      {msg.role === 'user' ? (
                        <p className="text-base leading-relaxed">{msg.content}</p>
                      ) : (
                        <div className="markdown-body prose prose-base max-w-none">
                          <MarkdownContent content={typeof msg.content === 'string' ? msg.content : String(msg.content || '')} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isChatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-xl px-6 py-4 flex items-center gap-3 shadow-sm border border-gray-200">
                      <div className="animate-bounce h-3 w-3 bg-gray-400 rounded-full"></div>
                      <div className="animate-bounce h-3 w-3 bg-gray-400 rounded-full delay-100"></div>
                      <div className="animate-bounce h-3 w-3 bg-gray-400 rounded-full delay-200"></div>
                      <span className="text-sm text-gray-500 ml-2">Consultant is thinking...</span>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input - Expanded */}
              <div className="p-6 border-t border-gray-200 bg-white">
              {chatError && (
                <div className="mb-3 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                  {chatError}
                </div>
              )}
              
              {!isInPipeline && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 text-center">
                    <span className="font-medium">AI Consultant requires Pipeline access.</span>
                    <button
                      onClick={handleAddToPipeline}
                      disabled={pipelineLoading}
                      className="ml-2 underline hover:text-amber-900"
                    >
                      {pipelineLoading ? 'Adding...' : 'Add to Pipeline'}
                    </button>
                  </p>
                </div>
              )}
              
              {voiceError && isVoiceSupported && (
                <div className="mb-3 text-sm text-amber-600 bg-amber-50 px-4 py-2 rounded">
                  {voiceError}
                </div>
              )}
              
              <div className="flex gap-3">
                <input
                  type="text"
                  value={chatInput + (isListening && interimTranscript ? ' ' + interimTranscript : '')}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={!isInPipeline 
                    ? "Add to Pipeline to use AI Consultant..." 
                    : isListening
                      ? "Listening..."
                      : isChatLoading 
                        ? "Consultant is thinking..." 
                        : "Type your question or use voice input..."
                  }
                  disabled={isChatLoading || !isInPipeline}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-100 transition-all"
                />
                
                {isVoiceSupported && isInPipeline && (
                  <button
                    onClick={isListening ? handleVoiceSend : startListening}
                    disabled={isChatLoading}
                    className={`px-4 py-3 rounded-xl transition-colors ${
                      isListening 
                        ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } disabled:opacity-50`}
                    title={isListening ? 'Click to send voice message' : 'Use voice input'}
                  >
                    {isListening ? (
                      <StopIcon className="h-5 w-5" />
                    ) : (
                      <MicrophoneIcon className="h-5 w-5" />
                    )}
                  </button>
                )}
                
                <button
                  onClick={handleSendMessage}
                  disabled={!chatInput.trim() || isChatLoading || !isInPipeline}
                  className="px-6 py-3 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 flex items-center gap-2 font-medium transition-all"
                  title={!isInPipeline ? "Add to Pipeline to use AI Consultant" : "Send message"}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                  Send
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 ${isChatConnected ? 'text-green-600' : 'text-gray-400'}`}>
                    {isChatConnected ? '● WebSocket' : chatTransport === 'sse' ? '○ SSE' : '○ Connecting...'} 
                  </span>
                  <span>•</span>
                  <span>{isChatLoading ? 'Consultant is thinking...' : 'Ready'}</span>
                </span>
                <span>Uses {creditsUsed}/{creditsTotal} credits</span>
              </div>
            </div>
            </div>

            {/* Conversation History in Drawer - Absolute positioned */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="pointer-events-auto">
                <ConversationHistory
                  isOpen={showHistory}
                  onClose={() => setShowHistory(false)}
                  onSelect={(conversation) => {
                    loadConversation(conversation.id);
                    setShowHistory(false);
                  }}
                  currentConversationId={chatConversationId}
                  opportunityId={opportunity?.id}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Markdown table styles
const markdownStyles = `
.markdown-body table {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
}
.markdown-body th,
.markdown-body td {
  border: 1px solid #d1d5db;
  padding: 0.75rem;
  text-align: left;
}
.markdown-body th {
  background-color: #f3f4f6;
  font-weight: 600;
}
.markdown-body tr:nth-child(even) {
  background-color: #f9fafb;
}
.markdown-body h1 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1.5rem 0 1rem;
}
.markdown-body h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1.25rem 0 0.75rem;
}
.markdown-body h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem;
}
.markdown-body p {
  margin: 0.75rem 0;
}
.markdown-body ul,
.markdown-body ol {
  margin: 0.75rem 0;
  padding-left: 1.5rem;
}
.markdown-body li {
  margin: 0.25rem 0;
}
.markdown-body strong {
  font-weight: 600;
}
.markdown-body a {
  color: #0ea5e9;
  text-decoration: none;
}
.markdown-body a:hover {
  text-decoration: underline;
}
.markdown-body blockquote {
  border-left: 4px solid #0ea5e9;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #4b5563;
}
.markdown-body code {
  background-color: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.875rem;
}
.markdown-body pre {
  background-color: #f3f4f6;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1rem 0;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleId = 'markdown-body-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = markdownStyles;
    document.head.appendChild(style);
  }
}

export default OpportunityDetail;

